/**
 * Direct message / notification operations — list, send, mark read, delete.
 *
 * All message content is E2E encrypted using ECDH (X25519 + XSalsa20-Poly1305).
 * Encryption keys are derived from the user's Ed25519 signing keypair.
 */

import type { ArbiClient, KeyPair, components } from '@arbidocs/client'
import { requireData, requireOk } from '../errors.js'
import { listContacts } from './contacts.js'
import { listAgents } from './agents.js'
import { listWorkspaceUsers } from './workspaces.js'

type NotificationResponse = components['schemas']['NotificationResponse']

/** A resolved DM recipient: their external_id + curve25519 public key. */
export interface ResolvedRecipient {
  extId: string
  pubKey: string
}

// ── Crypto context ──────────────────────────────────────────────────────────

/**
 * Everything needed to encrypt/decrypt DMs.
 * Derive once per session, pass to all DM operations.
 */
export interface DmCryptoContext {
  /** User's X25519 encryption keypair (derived from Ed25519 signing key) */
  encryptionKeypair: KeyPair
  /** User's own external_id (to determine send vs receive direction) */
  userExtId: string
}

/**
 * Create a DM crypto context from a signing private key.
 *
 * @param arbi - ArbiClient instance (provides crypto utilities)
 * @param signingPrivateKeyBytes - Ed25519 signing private key (64 bytes)
 * @param userExtId - Current user's external_id
 */
export function createDmCryptoContext(
  arbi: ArbiClient,
  signingPrivateKeyBytes: Uint8Array,
  userExtId: string
): DmCryptoContext {
  const ed25519PublicKey = signingPrivateKeyBytes.slice(32, 64)
  const encryptionKeypair = arbi.crypto.deriveEncryptionKeypairFromSigning({
    publicKey: ed25519PublicKey,
    secretKey: signingPrivateKeyBytes,
  })
  return { encryptionKeypair, userExtId }
}

// ── Encrypt / Decrypt helpers ───────────────────────────────────────────────

/**
 * Encrypt a plaintext message for a recipient.
 *
 * @param arbi - ArbiClient instance
 * @param plaintext - Message content to encrypt
 * @param recipientEncryptionPubKeyBase64 - Recipient's X25519 public key (base64)
 * @param senderEncryptionSecretKey - Sender's X25519 secret key (Uint8Array)
 * @returns Base64-encoded ciphertext (nonce prepended)
 */
export async function encryptDmContent(
  arbi: ArbiClient,
  plaintext: string,
  recipientEncryptionPubKeyBase64: string,
  senderEncryptionSecretKey: Uint8Array
): Promise<string> {
  return arbi.crypto.encryptMessage(
    plaintext,
    recipientEncryptionPubKeyBase64,
    senderEncryptionSecretKey
  )
}

/**
 * Decrypt an encrypted notification message.
 *
 * Uses the sender/recipient public keys embedded in NotificationResponse
 * to determine the "other party" for ECDH decryption.
 *
 * @param arbi - ArbiClient instance
 * @param notification - The notification with encrypted content
 * @param crypto - DM crypto context for the current user
 * @returns Decrypted plaintext, or null if content is empty
 * @throws Error if decryption fails (wrong keys or tampered message)
 */
export async function decryptDmContent(
  arbi: ArbiClient,
  notification: NotificationResponse,
  crypto: DmCryptoContext
): Promise<string | null> {
  if (!notification.content) return null

  const isSent = notification.sender.external_id === crypto.userExtId
  const otherParty = isSent ? notification.recipient : notification.sender

  return arbi.crypto.decryptMessage(
    notification.content,
    otherParty.encryption_public_key,
    crypto.encryptionKeypair.secretKey
  )
}

/**
 * Decrypt a batch of notifications, replacing content in-place.
 * Failed decryptions get content set to '[Decryption failed]'.
 *
 * @returns New array with decrypted content
 */
export async function decryptDmBatch(
  arbi: ArbiClient,
  notifications: NotificationResponse[],
  crypto: DmCryptoContext
): Promise<NotificationResponse[]> {
  const results: NotificationResponse[] = []
  for (const n of notifications) {
    if (n.type !== 'user_message' || !n.content) {
      results.push(n)
      continue
    }
    try {
      const plaintext = await decryptDmContent(arbi, n, crypto)
      results.push({ ...n, content: plaintext })
    } catch {
      results.push({ ...n, content: '[Decryption failed]' })
    }
  }
  return results
}

// ── API operations ──────────────────────────────────────────────────────────

/**
 * List DMs (raw — content is encrypted ciphertext).
 */
export async function listDMs(arbi: ArbiClient) {
  return requireData(await arbi.fetch.GET('/v1/notifications/'), 'Failed to fetch messages')
}

/**
 * List and decrypt DMs in one call.
 */
export async function listDecryptedDMs(arbi: ArbiClient, crypto: DmCryptoContext) {
  const raw = await listDMs(arbi)
  return decryptDmBatch(arbi, raw, crypto)
}

/**
 * List the current user's durable activity feed, newest first.
 *
 * Activity notifications are backend-originated, to-self log lines (recipient ==
 * sender == the acting user) that are always read — they never appear in the
 * normal DM/notification feed or the unread badge. Reading them requires the
 * explicit `type=activity` filter, which this helper always sets.
 */
export async function listActivity(
  arbi: ArbiClient,
  options?: { limit?: number; offset?: number }
) {
  return requireData(
    await arbi.fetch.GET('/v1/notifications/', {
      params: {
        query: {
          type: 'activity',
          ...(options?.limit != null ? { limit: options.limit } : {}),
          ...(options?.offset != null ? { offset: options.offset } : {}),
        },
      },
    }),
    'Failed to fetch activity'
  )
}

/**
 * Resolve a recipient reference (email OR `usr-`/`agt-` external_id) into the
 * `{ extId, pubKey }` an encrypted send needs. Walks three directories in order
 * — contacts, parent-owned agents, current-workspace members — and stops at the
 * first hit with a usable curve25519 public key. Returns `null` when nothing
 * matches so callers can surface a recovery hint.
 *
 * This is the single resolution path shared by the app, the CLI and seeding —
 * no consumer should re-implement directory lookups.
 */
export async function resolveRecipient(
  arbi: ArbiClient,
  ref: string
): Promise<ResolvedRecipient | null> {
  const isEmail = ref.includes('@')
  // Both keys exist (as strings) on ContactResponse, UserResponse and the
  // nested workspace-member user, so indexing by `matchKey` stays fully typed.
  const matchKey: 'email' | 'external_id' = isEmail ? 'email' : 'external_id'

  // Contacts: prefer the embedded user record's pubkey; fall back to the
  // contact-level external_id when the contact hasn't registered yet.
  const contacts = await listContacts(arbi)
  const contactMatch = contacts.find((c) =>
    isEmail ? c.email === ref : c.user?.external_id === ref || c.external_id === ref
  )
  if (contactMatch?.user?.encryption_public_key) {
    return {
      extId: contactMatch.user.external_id,
      pubKey: contactMatch.user.encryption_public_key,
    }
  }

  // Agents: parent-owned child identities. Wrapped because workspace-scoped
  // tokens can't list cross-workspace agents.
  try {
    const agents = await listAgents(arbi)
    const agentMatch = agents.find((a) => a[matchKey] === ref)
    if (agentMatch?.encryption_public_key) {
      return { extId: agentMatch.external_id, pubKey: agentMatch.encryption_public_key }
    }
  } catch {
    // Agent listing not available in this auth context — keep going.
  }

  // Workspace members: catches peers we share a workspace with but haven't
  // explicitly added as contacts. The identity lives on the nested `user`.
  try {
    const wsUsers = await listWorkspaceUsers(arbi)
    const wsMatch = wsUsers.find((wu) => wu.user[matchKey] === ref)
    if (wsMatch?.user.encryption_public_key) {
      return { extId: wsMatch.user.external_id, pubKey: wsMatch.user.encryption_public_key }
    }
  } catch {
    // Workspace-user listing not available — fall through to null.
  }

  return null
}

/**
 * Send an E2E encrypted DM.
 *
 * Encrypts plaintext content for each recipient using their public key.
 * Requires recipient public keys — fetch via contacts or user lookup first.
 *
 * Returns the created notifications with their content as **plaintext**, not
 * ciphertext — matching what `listDecryptedDMs` returns. The sender supplied the
 * text, so it is handed straight back: a caller must never have to re-fetch and
 * re-decrypt its own ciphertext just to render the message it has just sent.
 *
 * @param arbi - ArbiClient instance
 * @param messages - Array of { recipient_ext_id, content (plaintext), recipient_encryption_public_key }
 * @param crypto - DM crypto context for the sender
 */
export async function sendEncryptedDM(
  arbi: ArbiClient,
  messages: Array<{
    recipient_ext_id: string
    content: string
    recipient_encryption_public_key: string
  }>,
  crypto: DmCryptoContext
): Promise<NotificationResponse[]> {
  const plaintextFor = new Map(messages.map((m) => [m.recipient_ext_id, m.content]))
  const encrypted = await Promise.all(
    messages.map(async (msg) => ({
      recipient_ext_id: msg.recipient_ext_id,
      content: await encryptDmContent(
        arbi,
        msg.content,
        msg.recipient_encryption_public_key,
        crypto.encryptionKeypair.secretKey
      ),
    }))
  )
  const created = requireData(
    await arbi.fetch.POST('/v1/notifications/', { body: { messages: encrypted } }),
    'Failed to send message'
  )
  return created.map((n) => ({
    ...n,
    content: plaintextFor.get(n.recipient.external_id) ?? n.content,
  }))
}

/**
 * Ask another user to grant you workspace access.
 *
 * Creates a `workspace_access_request` notification. The `content` is an
 * opaque base64 blob, E2E-encrypted with the requester↔recipient shared key
 * (same scheme as a DM), carrying the requester's live session public key
 * (`sessionPubkeyB64`) so the recipient can mint a temporary, session-scoped
 * grant, plus an optional note.
 *
 * `workspaceExtId` is optional and only meaningful for API/SDK/CLI callers who
 * already know a workspace id (obtained out-of-band); omit it and the recipient
 * picks the workspace.
 */
export async function requestWorkspaceAccess(
  arbi: ArbiClient,
  params: {
    recipientExtId: string
    recipientEncryptionPublicKey: string
    sessionPubkeyB64: string
    note?: string
    workspaceExtId?: string
  },
  crypto: DmCryptoContext
) {
  const payload = JSON.stringify({
    session_pubkey_b64: params.sessionPubkeyB64,
    note: params.note?.trim() || undefined,
  })
  const content = await encryptDmContent(
    arbi,
    payload,
    params.recipientEncryptionPublicKey,
    crypto.encryptionKeypair.secretKey
  )
  return requireData(
    await arbi.fetch.POST('/v1/notifications/access-request', {
      body: {
        recipient_ext_id: params.recipientExtId,
        content,
        ...(params.workspaceExtId ? { workspace_ext_id: params.workspaceExtId } : {}),
      },
    }),
    'Failed to request workspace access'
  )
}

/**
 * Send a plaintext DM (no encryption).
 * @deprecated Use sendEncryptedDM instead.
 */
export async function sendDM(
  arbi: ArbiClient,
  messages: Array<{ recipient_ext_id: string; content: string }>
) {
  return requireData(
    await arbi.fetch.POST('/v1/notifications/', {
      body: { messages },
    }),
    'Failed to send message'
  )
}

export async function markRead(arbi: ArbiClient, messageIds: string[]) {
  return requireData(
    await arbi.fetch.PATCH('/v1/notifications/', {
      body: { updates: messageIds.map((id) => ({ external_id: id, read: true })) },
    }),
    'Failed to update messages'
  )
}

export async function deleteDMs(arbi: ArbiClient, messageIds: string[]) {
  requireOk(
    await arbi.fetch.DELETE('/v1/notifications/', { body: { external_ids: messageIds } }),
    'Failed to delete messages'
  )
}
